import { FC, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import moment from 'moment'
import { ReactComponent as ArrowRightIcon } from 'src/assets/svg/arrow-right-line.svg'
import { ReactComponent as BuildingIcon } from 'src/assets/svg/companyProfile/carbon_building.svg'
import { ReactComponent as PinIcon } from 'src/assets/svg/locationPin.svg'
import { SentimentDistributionCard } from 'src/components/SentimentDistributionCard'
import useOutsideClick from 'src/hooks/useOutsideClick'
import { PLATFORM_ORDER } from 'src/pages/ReviewAnalysis/constants'
import { getPlatformIcon } from 'src/pages/ReviewAnalysis/platformIcons'
import {
  Facility,
  FacilityCard,
  SentimentBreakdown,
} from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import { TruncatedText } from 'src/pages/ReviewAnalysis/TruncatedText'
import styles from './facilityAnalysisCard.module.scss'
import { FacilityActions } from '../FacilityActions'
import {
  SentimentData,
  SentimentDonut,
  badgeClass,
  getRatingLabel,
  renderStars,
} from '../PlatformAnalysisCard'
import { ReviewStatsCards } from '../ReviewStatsCards'

// A branch is one location (the card title) across every platform it is on.
interface Branch {
  cards: FacilityCard[]
  rating: number | null
  reviewCount: number
  sentiment: SentimentData
  title: string
}

const EMPTY_SENTIMENT: SentimentData = {
  negative: 0,
  neutral: 0,
  positive: 0,
  total: 0,
}

// Rating averaged over the cards' reviews (a card with more reviews weighs
// more); sentiment counts summed.
const rollUp = (cards: FacilityCard[]) => {
  let reviews = 0
  let ratingSum = 0
  const sentiment = { ...EMPTY_SENTIMENT }
  for (const card of cards) {
    const count = card.reviewCount ?? 0
    if (card.rating != null && count > 0) {
      reviews += count
      ratingSum += card.rating * count
    }
    if (card.sentiment) {
      sentiment.positive += card.sentiment.positive
      sentiment.neutral += card.sentiment.neutral
      sentiment.negative += card.sentiment.negative
      sentiment.total += card.sentiment.total
    }
  }
  return {
    rating: reviews > 0 ? ratingSum / reviews : null,
    reviewCount: reviews,
    sentiment,
  }
}

const toBreakdown = (s: SentimentData): SentimentBreakdown => {
  const share = (n: number) => (s.total ? Math.round((n / s.total) * 100) : 0)
  return {
    negative: { count: s.negative, percentage: share(s.negative) },
    neutral: { count: s.neutral, percentage: share(s.neutral) },
    positive: { count: s.positive, percentage: share(s.positive) },
  }
}

const latestUpdate = (cards: FacilityCard[]): string | null => {
  const times = cards
    .map((c) => (c.lastUpdate ? new Date(c.lastUpdate).getTime() : NaN))
    .filter((t) => !Number.isNaN(t))
  return times.length ? moment(Math.max(...times)).format('MMM D') : null
}

const platformRank = (card: FacilityCard) => {
  const i = PLATFORM_ORDER.indexOf(card.platform)
  return i === -1 ? PLATFORM_ORDER.length : i
}

// Per-branch menu: the edit / remove actions each platform card had.
const BranchMenu: FC<{
  cards: FacilityCard[]
  onDeleteCard: (card: FacilityCard) => void
  onEditCard: (card: FacilityCard) => void
}> = ({ cards, onDeleteCard, onEditCard }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useOutsideClick({ onOutsideClick: () => setOpen(false), ref })
  const run = (action: () => void) => {
    setOpen(false)
    action()
  }

  return (
    <div className={styles.branchMenu} ref={ref}>
      <button
        aria-label="Branch actions"
        className={styles.branchMenuBtn}
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        type="button"
      >
        <span />
        <span />
        <span />
      </button>
      {open && (
        <div className={styles.branchMenuDropdown}>
          {cards.map((card) => (
            <div className={styles.branchMenuGroup} key={card.id}>
              <span className={styles.branchMenuLabel}>
                {card.providerName}
              </span>
              <button
                className={styles.branchMenuItem}
                onClick={() => run(() => onEditCard(card))}
                type="button"
              >
                Edit
              </button>
              <button
                className={classNames(
                  styles.branchMenuItem,
                  styles.branchMenuDelete
                )}
                onClick={() => run(() => onDeleteCard(card))}
                type="button"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const BranchRow: FC<{
  branch: Branch
  facilityName: string
  onDeleteCard: (card: FacilityCard) => void
  onEditCard: (card: FacilityCard) => void
  onView: () => void
}> = ({ branch, facilityName, onDeleteCard, onEditCard, onView }) => {
  const hasData = branch.rating != null
  const label = hasData ? getRatingLabel(branch.rating as number) : null

  return (
    <div className={styles.branchRow}>
      <div className={styles.pinTile}>
        <PinIcon />
      </div>
      <div className={styles.branchMain}>
        <TruncatedText className={styles.branchTitle} text={branch.title} />
        <p className={styles.branchFacility}>{facilityName}</p>
        <div className={styles.platformChips}>
          {branch.cards.map((card) => {
            const Icon = getPlatformIcon(card.platform)
            const pending = card.status && card.status !== 'ready'
            // The chip opens the location on its platform (e.g. Google Maps).
            const link = card.links?.[0]
            const chip = {
              className: classNames(
                styles.platformChip,
                pending && styles.platformChipPending,
                link && styles.platformChipLink
              ),
              title:
                card.status === 'failed'
                  ? 'Data collection failed'
                  : pending
                    ? 'Collecting reviews'
                    : link
                      ? `Open on ${card.providerName}`
                      : undefined,
            }
            const content = (
              <>
                <Icon className={styles.platformChipIcon} />
                {card.providerName}
              </>
            )
            return link ? (
              <a
                {...chip}
                href={link}
                key={card.id}
                rel="noopener noreferrer"
                target="_blank"
              >
                {content}
              </a>
            ) : (
              <span {...chip} key={card.id}>
                {content}
              </span>
            )
          })}
        </div>
      </div>

      <div className={styles.branchRating}>
        <div className={styles.branchRatingRow}>
          <span className={styles.avgLabel}>avg. rating</span>
          <span className={styles.ratingValue}>
            {hasData ? (branch.rating as number).toFixed(1) : '0.0'}
          </span>
          <span className={styles.stars}>
            {renderStars(hasData ? (branch.rating as number) : 0)}
          </span>
          {label && (
            <span className={classNames(styles.badge, badgeClass(label))}>
              {label}
            </span>
          )}
        </div>
        <p className={styles.basedOn}>
          Based on <b>{branch.reviewCount.toLocaleString()}</b> Reviews
        </p>
      </div>

      <div className={styles.branchDonut}>
        <SentimentDonut data={branch.sentiment} />
      </div>

      {/* always laid out, so rows without data keep the same columns */}
      <button
        className={classNames(
          styles.branchView,
          !hasData && styles.branchViewHidden
        )}
        disabled={!hasData}
        onClick={onView}
        type="button"
      >
        View Details <ArrowRightIcon className={styles.arrowIcon} />
      </button>
      <BranchMenu
        cards={branch.cards}
        onDeleteCard={onDeleteCard}
        onEditCard={onEditCard}
      />
    </div>
  )
}

interface FacilityAnalysisCardProps {
  facility: Facility
  onAddLocation: () => void
  onDelete: () => void
  onDeleteCard: (card: FacilityCard) => void
  onEditCard: (card: FacilityCard) => void
  onRename: () => void
  onViewBranch: (title: string) => void
  onViewFacility: () => void
  periodLabel?: string
}

/**
 * One facility in the brand page's Facility Analysis: headline numbers, the
 * platforms it is on, and its branches (each location across its platforms).
 * Built from the overview's per-location-per-platform cards, so it needs no
 * extra request.
 */
export const FacilityAnalysisCard: FC<FacilityAnalysisCardProps> = ({
  facility,
  onAddLocation,
  onDelete,
  onDeleteCard,
  onEditCard,
  onRename,
  onViewBranch,
  onViewFacility,
  periodLabel,
}) => {
  const branches = useMemo<Branch[]>(() => {
    const byTitle = new Map<string, FacilityCard[]>()
    for (const card of facility.cards) {
      const list = byTitle.get(card.title) ?? []
      list.push(card)
      byTitle.set(card.title, list)
    }
    return Array.from(byTitle.entries())
      .map(([title, cards]) => ({
        cards: [...cards].sort((a, b) => platformRank(a) - platformRank(b)),
        title,
        ...rollUp(cards),
      }))
      .sort((a, b) => b.reviewCount - a.reviewCount)
  }, [facility.cards])

  const totals = useMemo(() => rollUp(facility.cards), [facility.cards])
  const platforms = useMemo(
    () =>
      PLATFORM_ORDER.filter((p) =>
        facility.cards.some((card) => card.platform === p)
      ),
    [facility.cards]
  )
  const lastUpdate = latestUpdate(facility.cards)
  const branchCount = branches.length

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.buildingTile}>
          <BuildingIcon />
        </div>
        <div className={styles.headerText}>
          <p className={styles.facilityName}>
            Facility - <b>{facility.name}</b>
            {facility.category && (
              <span className={styles.categoryTag}>{facility.category}</span>
            )}
          </p>
          <p className={styles.headerMeta}>
            <PinIcon className={styles.metaPin} />
            {branchCount} {branchCount === 1 ? 'branch' : 'branches'}
            {lastUpdate && (
              <>
                <span className={styles.metaDivider} />
                Last update {lastUpdate}
              </>
            )}
          </p>
        </div>
        <FacilityActions
          onAddLocation={onAddLocation}
          onDelete={onDelete}
          onRename={onRename}
        />
      </div>

      <div className={styles.statsRow}>
        <ReviewStatsCards
          averageRating={totals.rating ?? undefined}
          combined
          periodLabel={periodLabel}
          totalReviews={totals.reviewCount}
          totalTitle="Reviews"
        />
        <SentimentDistributionCard data={toBreakdown(totals.sentiment)} />
        <div className={styles.platformsCard}>
          <div className={styles.platformsHeader}>
            <span>{platforms.length === 1 ? 'Platform' : 'Platforms'}</span>
            <span className={styles.platformsCount}>{platforms.length}</span>
          </div>
          <div className={styles.platformsList}>
            {platforms.map((p) => {
              const Icon = getPlatformIcon(p)
              const card = facility.cards.find((c) => c.platform === p)
              return (
                <span className={styles.platformsItem} key={p}>
                  <Icon className={styles.platformsIcon} />
                  {card?.providerName ?? p}
                </span>
              )
            })}
          </div>
        </div>
      </div>

      <div className={styles.branchesHeader}>
        <h4 className={styles.branchesTitle}>Branches</h4>
        <span className={styles.branchesCount}>
          <PinIcon className={styles.metaPin} />
          {branchCount} {branchCount === 1 ? 'location' : 'locations'}
        </span>
      </div>
      <div className={styles.branchList}>
        {branches.map((branch) => (
          <BranchRow
            branch={branch}
            facilityName={facility.name}
            key={branch.title}
            onDeleteCard={onDeleteCard}
            onEditCard={onEditCard}
            onView={() => onViewBranch(branch.title)}
          />
        ))}
      </div>

      <div className={styles.footer}>
        <button
          className={styles.viewFacilityBtn}
          onClick={onViewFacility}
          type="button"
        >
          View Details
        </button>
      </div>
    </div>
  )
}
